import api from './axiosApi'
import type {
  PaginatedStoreModelsResponse,
  PaginatedStoreModelMembersResponse,
  PaginatedStoreGroupsResponse,
  PaginatedPlansResponse,
  StoreGroupMemberListResponse,
  StoreModelDetailRead,
  BulkOperationResponse,
  BulkUpdateStoreGroupsRequest,
  ModelType,
  ModelStatus,
  StoreModelCreate,
  StoreModelUpdate,
  ProcessingResponse,
  AllocationOverrideRequest,
  AllocationOverrideResponse,
  ApplyGroupingRequest,
} from '@/types'

interface SearchStoreModelsParams {
  search?: string
  skip?: number
  limit?: number
}

export async function searchStoreModels(
  params: SearchStoreModelsParams = {}
): Promise<PaginatedStoreModelsResponse> {
  const { search, skip = 0, limit = 20 } = params

  const response = await api.get<PaginatedStoreModelsResponse>(
    '/admin/store-models',
    {
      params: {
        search: search || undefined,
        status: 'ACTIVE',
        skip,
        limit,
      },
    }
  )

  return response.data
}

interface ListStoreModelsParams {
  search?: string
  model_type?: ModelType
  status?: ModelStatus
  sort?: string
  skip?: number
  limit?: number
}

export async function listStoreModels(
  params: ListStoreModelsParams = {}
): Promise<PaginatedStoreModelsResponse> {
  const { search, model_type, status, sort, skip = 0, limit = 20 } = params

  const response = await api.get<PaginatedStoreModelsResponse>(
    '/admin/store-models',
    {
      params: {
        search: search || undefined,
        model_type: model_type || undefined,
        status: status || undefined,
        sort: sort || undefined,
        skip,
        limit,
      },
    }
  )

  return response.data
}

interface GetStoreModelStoresParams {
  modelId: string
  skip?: number
  limit?: number
  search?: string
  sort?: string
  store_group_id?: string
}

export async function getStoreModelStores(
  params: GetStoreModelStoresParams
): Promise<PaginatedStoreModelMembersResponse> {
  const { modelId, skip = 0, limit = 10, search, sort, store_group_id } = params

  const response = await api.get<PaginatedStoreModelMembersResponse>(
    `/admin/store-models/${modelId}/stores`,
    {
      params: {
        skip,
        limit,
        search: search || undefined,
        sort: sort || undefined,
        store_group_id: store_group_id || undefined,
      },
    }
  )

  return response.data
}

export async function getStoreModel(
  modelId: string
): Promise<StoreModelDetailRead> {
  const response = await api.get<StoreModelDetailRead>(
    `/admin/store-models/${modelId}`
  )
  return response.data
}

export async function createStoreModel(
  payload: StoreModelCreate
): Promise<ProcessingResponse> {
  const response = await api.post<ProcessingResponse>(
    '/admin/store-models',
    payload
  )
  return response.data
}

export async function updateStoreModel(
  modelId: string,
  payload: StoreModelUpdate
): Promise<ProcessingResponse> {
  const response = await api.patch<ProcessingResponse>(
    `/admin/store-models/${modelId}`,
    payload
  )
  return response.data
}

export async function deleteStoreModel(modelId: string): Promise<void> {
  await api.delete(`/admin/store-models/${modelId}`)
}

export async function overrideAllocations(
  modelId: string,
  payload: AllocationOverrideRequest
): Promise<AllocationOverrideResponse> {
  const response = await api.patch<AllocationOverrideResponse>(
    `/admin/store-models/${modelId}/allocations`,
    payload
  )
  return response.data
}

export async function resetAllocations(
  modelId: string
): Promise<AllocationOverrideResponse> {
  const response = await api.post<AllocationOverrideResponse>(
    `/admin/store-models/${modelId}/allocations/reset`
  )
  return response.data
}

export async function triggerRecalculation(
  modelId: string
): Promise<ProcessingResponse> {
  const response = await api.post<ProcessingResponse>(
    `/admin/store-models/${modelId}/recalculations`
  )
  return response.data
}

export async function applyGrouping(
  modelId: string,
  payload: ApplyGroupingRequest
): Promise<ProcessingResponse> {
  const response = await api.post<ProcessingResponse>(
    `/admin/store-models/${modelId}/groups`,
    payload
  )
  return response.data
}

interface ListModelGroupsParams {
  modelId: string
  skip?: number
  limit?: number
  search?: string
}

export async function listModelGroups(
  params: ListModelGroupsParams
): Promise<PaginatedStoreGroupsResponse> {
  const { modelId, skip = 0, limit = 100, search } = params

  const response = await api.get<PaginatedStoreGroupsResponse>(
    `/admin/store-models/${modelId}/groups`,
    {
      params: {
        skip,
        limit,
        search: search || undefined,
      },
    }
  )

  return response.data
}

interface ListStoreModelPlansParams {
  modelId: string
  skip?: number
  limit?: number
  sort?: string
}

export async function listStoreModelPlans(
  params: ListStoreModelPlansParams
): Promise<PaginatedPlansResponse> {
  const { modelId, skip = 0, limit = 20, sort } = params

  const response = await api.get<PaginatedPlansResponse>(
    `/admin/store-models/${modelId}/plans`,
    {
      params: {
        skip,
        limit,
        sort: sort || undefined,
      },
    }
  )

  return response.data
}

export async function getModelGroupMembers(
  modelId: string,
  groupId: string,
  params: { skip?: number; limit?: number; search?: string } = {}
): Promise<StoreGroupMemberListResponse> {
  const { skip = 0, limit = 20, search } = params
  const response = await api.get<StoreGroupMemberListResponse>(
    `/admin/store-models/${modelId}/groups/${groupId}/stores`,
    { params: { skip, limit, search: search || undefined } }
  )
  return response.data
}

interface AssignStoreModelPayload {
  line_ids: string[]
  store_model_id: string
}

export async function assignStoreModel(
  planId: string,
  versionId: string,
  payload: AssignStoreModelPayload
): Promise<BulkOperationResponse> {
  const response = await api.post<BulkOperationResponse>(
    `/plans/${planId}/versions/${versionId}/lines/assign-store-model`,
    payload
  )

  return response.data
}

export async function unassignStoreModel(
  planId: string,
  versionId: string,
  payload: { line_ids: string[] }
): Promise<BulkOperationResponse> {
  const response = await api.post<BulkOperationResponse>(
    `/plans/${planId}/versions/${versionId}/lines/unassign-store-model`,
    payload
  )
  return response.data
}

export async function bulkUpdateStoreGroups(
  planId: string,
  versionId: string,
  payload: BulkUpdateStoreGroupsRequest
): Promise<BulkOperationResponse> {
  const response = await api.put<BulkOperationResponse>(
    `/plans/${planId}/versions/${versionId}/lines/bulk-store-groups`,
    payload
  )
  return response.data
}
